<template>
  <div class="custom-stepper">
    <button
      type="button"
      class="custom-stepper__minus"
      :class="{'custom-stepper__minus--disabled': currentValue <= min}"
      @touchstart.prevent="onMinus"
    />
    <input
      v-model="inputValue"
      type="tel"
      role="spinbutton"
      inputmode="numeric"
      :aria-valuemax="max"
      :aria-valuemin="min"
      :aria-valuenow="currentValue"
      class="custom-stepper__input"
      @input="onInput"
      @blur="onInputBlur"
    >
    <button
      type="button"
      class="custom-stepper__plus"
      :class="{'custom-stepper__plus--disabled': currentValue >= max}"
      @touchstart.prevent="onPlus"
    />
  </div>
</template>

<script>
export default {
  name: 'CustomStepper',
  props: {
    min: {
      type: Number,
      default: 1,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    modelValue: {
      type: Number,
      default: 1,
    },
    step: {
      type: Number,
      default: 1,
    },
    onChange: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      currentValue: this.modelValue,
      inputValue: this.modelValue.toString(),
    };
  },
  watch: {
    modelValue: {
      handler(val) {
        this.currentValue = val;
        this.inputValue = val.toString();
      },
      immediate: true,
    },
  },
  methods: {
    onMinus() {
      if (this.currentValue <= this.min) {
        return;
      }

      const newValue = Math.max(this.currentValue - this.step, this.min);
      this.updateValue(newValue);
    },

    onPlus() {
      if (this.currentValue >= this.max) {
        return;
      }

      const newValue = Math.min(this.currentValue + this.step, this.max);
      this.updateValue(newValue);
    },

    onInput() {
      // 过滤非数字字符
      this.inputValue = this.inputValue.replace(/[^0-9]/g, '');

      // 转换为数字
      const value = this.inputValue === '' ? this.min : parseInt(this.inputValue, 10);

      this.updateValue(value, true);
    },

    onInputBlur() {
      let value = parseInt(this.inputValue, 10);

      if (Number.isNaN(value)) {
        value = this.min;
      }

      // 检查用户输入是否超出范围
      const adjustedValue = Math.max(Math.min(value, this.max), this.min);

      this.updateValue(adjustedValue, true);
    },

    updateValue(value, force = false) {
      // 如果值相同且不是强制更新，则跳过
      if (value === this.currentValue && !force) {
        return;
      }

      this.currentValue = value;
      this.inputValue = value.toString();
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },
  },
};
</script>

<style lang="less">
.custom-stepper {
  font-size: 0;
  user-select: none;
}

.custom-stepper__minus,
.custom-stepper__plus {
  position: relative;
  box-sizing: border-box;
  width: 0.28rem;
  height: 0.28rem;
  margin: 0;
  padding: 0;
  color: #323233;
  vertical-align: middle;
  background-color: #f2f3f5;
  border: 0;
  cursor: pointer;
}

.custom-stepper__minus::before,
.custom-stepper__plus::before {
  width: 50%;
  height: 0.01rem;
}

.custom-stepper__minus::after,
.custom-stepper__plus::after {
  width: 0.01rem;
  height: 50%;
}

.custom-stepper__minus::after,
.custom-stepper__minus::before,
.custom-stepper__plus::after,
.custom-stepper__plus::before {
  position: absolute;
  top: 50%;
  left: 50%;
  background-color: currentcolor;
  transform: translate(-50%, -50%);
  content: '';
}

.custom-stepper__minus:active,
.custom-stepper__plus:active {
  background-color: #e8e8e8;
}

.custom-stepper__minus--disabled,
.custom-stepper__plus--disabled {
  color: #c8c9cc;
  background-color: #f7f8fa;
  cursor: not-allowed;
}

.custom-stepper__minus--disabled:active,
.custom-stepper__plus--disabled:active {
  background-color: #f7f8fa;
}

.custom-stepper__minus {
  border-radius: 0.04rem 0 0 0.04rem;
}

.custom-stepper__minus::after {
  display: none;
}

.custom-stepper__plus {
  border-radius: 0 0.04rem 0.04rem 0;
}

.custom-stepper__input {
  box-sizing: border-box;
  width: 0.32rem;
  height: 0.28rem;
  margin: 0 0.02rem;
  padding: 0;
  color: #323233;
  font-size: 0.14rem;
  line-height: normal;
  text-align: center;
  vertical-align: middle;
  background-color: #f2f3f5;
  border: 0;
  border-width: 0.01rem 0;
  border-radius: 0;
  appearance: none;
}

.custom-stepper__input:disabled {
  color: #c8c9cc;
  background-color: #f2f3f5;
  -webkit-text-fill-color: #c8c9cc;
  opacity: 1;
}

.custom-stepper__input:read-only {
  cursor: default;
}

.custom-stepper--round .custom-stepper__input {
  background-color: transparent;
}

.custom-stepper--round .custom-stepper__minus,
.custom-stepper--round .custom-stepper__plus {
  border-radius: 100%;
}

.custom-stepper--round .custom-stepper__minus:active,
.custom-stepper--round .custom-stepper__plus:active {
  opacity: .7;
}

.custom-stepper--round .custom-stepper__minus--disabled,
.custom-stepper--round .custom-stepper__minus--disabled:active,
.custom-stepper--round .custom-stepper__plus--disabled,
.custom-stepper--round .custom-stepper__plus--disabled:active {
  opacity: .3;
}

.custom-stepper--round .custom-stepper__plus {
  color: #fff;
  background-color: #ee0a24;
}

.custom-stepper--round .custom-stepper__minus {
  color: #ee0a24;
  background-color: #fff;
  border: 0.01rem solid #ee0a24;
}
</style>
